import { Component, type ReactElement, createRef } from "react";

import { ActionButtonIntent, Card, CardRowSimple, type GridCard, LoadingSpinner } from "widgets";

import InMemoryTableWithSelectionModel from "../../core/grid/InMemoryTableWithSelectionModel.js";
import { ConfirmationDialog } from "../../core/overlays/Dialogs.js";
import { ModalControlledOverlay } from "../../core/overlays/index.js";
import { type Project, type User } from "@/graphql.ts";
import { getProjectLabel } from "@/redux/modules/config.ts";
import { type IUpdateProjectRoleInput } from "@/redux/modules/project.ts";
import { CELL_TYPE, PROJECT_ROLE } from "@/settings/settings.ts";

export interface IMembersCardProps extends GridCard {
  project: Project;
  ownUser: User;
  updateProjectRole: (input: IUpdateProjectRoleInput) => Promise<any>;
  removeUserHandler: (user: User) => void;
}

interface IMembersCardState {
  showEditConfirmationDialog?: boolean;
  deleteUser: User | null;
}

const INITIAL_STATE: IMembersCardState = {
  showEditConfirmationDialog: false,
  deleteUser: null,
};

export default class MembersCard extends Component<IMembersCardProps, IMembersCardState> {
  // client side grid component instance for triggering sizeColumnsToFit()
  private tableInstance = createRef<InMemoryTableWithSelectionModel>();

  private static projectMembersTableColumns: any[] = [
    {
      name: "Username",
      field: "user.username",
      cellType: CELL_TYPE.STRING,
    },
    {
      name: "First Name",
      field: "user.firstName",
      cellType: CELL_TYPE.STRING,
    },
    {
      name: "Last Name",
      field: "user.lastName",
      cellType: CELL_TYPE.STRING,
    },
    {
      name: "Role",
      field: "roleId",
      cellType: CELL_TYPE.STRING,
      comparator: (valueA: number, valueB: number): number => {
        // ensure sort order admin - manager - user by moving user to the numerical end
        const derivedValueA: number = valueA === PROJECT_ROLE.USER ? 4 : valueA;
        const derivedValueB: number = valueB === PROJECT_ROLE.USER ? 4 : valueB;
        return derivedValueA - derivedValueB;
      },
    },
    {
      name: "Actions",
      field: "projectMemberActions",
      cellType: CELL_TYPE.STRING,
      sortable: false,
    },
  ];

  state = INITIAL_STATE;

  // called when the admin icon was clicked in the name cell
  onToggleAdminRole = (userId: number, setAdmin: boolean): void => {
    // There are two distinct scenarios:
    // - if the user attempts to remove his own admin role, we show a confirmation dialog
    // - otherwise (user attempts to switch other user's admin role on or off) we do this directly
    if (userId === this.props.ownUser.id && !setAdmin) {
      this.setState({ ...this.state, showEditConfirmationDialog: true });
    } else {
      const updateProjectRoleInput: IUpdateProjectRoleInput = {
        projectId: this.props.project.id,
        userId: userId,
        newRoleId: setAdmin ? PROJECT_ROLE.ADMIN : PROJECT_ROLE.USER,
        ownUserId: this.props.ownUser.id,
      };

      this.props.updateProjectRole(updateProjectRoleInput);
    }
  };

  // called when the manager icon was clicked in the name cell
  onToggleManagerRole = (userId: number, setManager: boolean): void => {
    const updateProjectRoleInput: IUpdateProjectRoleInput = {
      projectId: this.props.project.id,
      userId: userId,
      newRoleId: setManager ? PROJECT_ROLE.MANAGER : PROJECT_ROLE.USER,
      ownUserId: this.props.ownUser.id,
    };
    this.props.updateProjectRole(updateProjectRoleInput);
  };

  // handler for closing the "remove my admin role" confirmation dialog by cancel
  onCancelRemoveMyAdminRole = () => {
    this.setState({ ...this.state, showEditConfirmationDialog: false });
  };

  // handler called when the "remove my admin role" confirmation dialog was accepted
  onRemoveMyAdminRole = (): Promise<any> => {
    return this.props
      .updateProjectRole({
        projectId: this.props.project.id,
        userId: this.props.ownUser.id,
        newRoleId: PROJECT_ROLE.USER,
        ownUserId: this.props.ownUser.id,
      })
      .then(() => {
        this.setState({ showEditConfirmationDialog: false });
      });
  };

  createRowData = (project: Project) => {
    return project.projectMembers.map((m) => {
      return {
        id: m.user.id,
        user: { ...m.user }, // we have to clone the user because of a bug in ag-grid 13 (AG-771)
        roleId: m.roleId,
        project: project,
        onDeleteMember: this.props.removeUserHandler,
        onToggleAdminRole: this.onToggleAdminRole,
        onToggleManagerRole: this.onToggleManagerRole,
        projectMemberActions: m.roleId, // Required value for the pseudo column so that the CellDataChecker (DEV only) does not complain
      };
    });
  };

  buildEditConfirmationModalDialog = (): ReactElement => {
    // This dialog only pops up if an admin attempts to remove his own admin role.
    if (!this.state.showEditConfirmationDialog) {
      return null;
    }

    return (
      <ModalControlledOverlay close={this.onCancelRemoveMyAdminRole} isOpen={true}>
        {({ close }) => (
          <ConfirmationDialog
            key={"project-edit-confirmation-dialog"}
            headerProps={{
              title: "Remove Admin",
            }}
            mainActionButtonProps={{
              children: `Remove`,
              onPress: () => this.onRemoveMyAdminRole().then(() => close()),
              intent: ActionButtonIntent.Danger,
            }}
            closeFn={close}
          >
            <div>Are you sure you want to remove your admin role for this {getProjectLabel(false, false)}?</div>
            <div>You will not be able to make any additional changes to this {getProjectLabel(false, false)}.</div>
          </ConfirmationDialog>
        )}
      </ModalControlledOverlay>
    );
  };

  render() {
    const editConfirmationDialog: ReactElement = this.buildEditConfirmationModalDialog();

    return (
      <Card key="members-card" title={`${getProjectLabel(true, false)} Members`}>
        {this.props.project?.isPublic && (
          <CardRowSimple>
            The members of this {getProjectLabel(false, false)} are based on user group '
            {this.props.project?.userGroup?.name}'. It is not possible to add or remove members.
          </CardRowSimple>
        )}
        {this.props.project?.isPrivateWorkspace && (
          <CardRowSimple>
            This is a private workspace. It is not possible to add or remove members. You are the only member.
          </CardRowSimple>
        )}
        {editConfirmationDialog}
        {this.props.project?.projectMembers?.length ? (
          <InMemoryTableWithSelectionModel
            ref={this.tableInstance}
            columns={MembersCard.projectMembersTableColumns}
            rowGridData={this.createRowData(this.props.project)}
            uniqueGridIdentifier={"project-members"}
            useSelectionModel={false}
            gridProps={{ domLayout: "autoHeight" }}
            className={"with-outside-borders"}
          />
        ) : (
          <LoadingSpinner />
        )}
      </Card>
    );
  }
}
